import { formatTimeAgo } from "@/shared/utils/helperFunctions";

/** Relative time label, or em dash if missing/invalid */
export function safeFormatTimeAgo(iso) {
  if (!iso) return "—";
  const s = formatTimeAgo(iso);
  return s === "Invalid date" ? "—" : s;
}

export function formatFollowUpDate(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function formatFetchDate(value) {
  const [year, month, day] = String(value || "")
    .slice(0, 10)
    .split("-")
    .map(Number);
  if (!year || !month || !day) return "—";

  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

export function formatLastSyncedAt(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function companyDomainHref(domain) {
  const value = String(domain || "").trim();
  if (!value) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/** Logo URL safe for CommonAvatar: only http(s), skip junk placeholders */
export function sanitizeCompanyLogoUrl(url) {
  if (!url || typeof url !== "string") return undefined;
  const t = url.trim();
  if (!/^https?:\/\//i.test(t)) return undefined;
  if (/unknown|not found|n\/a/i.test(t)) return undefined;
  // Research sometimes returns the company homepage instead of an image. A bare origin
  // is never an image, and requesting it logs ERR_BLOCKED_BY_RESPONSE in the console
  // before the avatar falls back, so drop it here. Paths are kept as-is: plenty of
  // legitimate logo URLs are extensionless CDN or image-proxy links.
  try {
    const { pathname, search } = new URL(t);
    if (!search && pathname.replace(/\/+$/, "") === "") return undefined;
  } catch {
    return undefined;
  }
  return t;
}

const TOP_ENGAGED_METRIC_CHANNELS = ["email", "linkedin", "whatsapp"];

const CHANNEL_TITLE = {
  email: "Email",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
};

const FOLLOW_UP_TYPE_LABELS = {
  scheduled: "Scheduled",
  due: "Due today",
  overdue: "Overdue",
  no_response: "No response",
  contact_engagement: "Contact engagement",
  engagement_spike: "Engagement spike",
  dormant: "Dormant account",
  ai_suggestion: "AI suggestion",
};

/** Normalize API `signal_counts` for list cards */
export function normalizeTopEngagedSignalCounts(signalCounts) {
  if (!signalCounts || typeof signalCounts !== "object") {
    return { email: 0, linkedin: 0, whatsapp: 0 };
  }
  return {
    email: Number(signalCounts.email) || 0,
    linkedin: Number(signalCounts.linkedin) || 0,
    whatsapp: Number(signalCounts.whatsapp) || 0,
  };
}

const TOP_ENGAGED_SCORE_BREAKDOWN_LABELS = [
  ["email_open", "Email opens"],
  ["email_click", "Email clicks"],
  ["message_read", "Message reads"],
  ["reply", "Replies"],
  ["linkedin_connect", "LinkedIn connections"],
  ["reply_floor", "Reply baseline"],
];

export function summarizeTopEngagedScoreBreakdown(scoreBreakdown) {
  if (!scoreBreakdown || typeof scoreBreakdown !== "object") return null;

  const parts = TOP_ENGAGED_SCORE_BREAKDOWN_LABELS.map(([key, label]) => {
    const item = scoreBreakdown[key];
    const count = Number(item?.count) || 0;
    const points = Number(item?.points) || 0;
    if (!count && !points) return null;
    return `${label}: ${count} (+${points})`;
  }).filter(Boolean);

  return parts.length ? parts.join(" · ") : null;
}

/** Channel with highest signal count (order tie-break: email → linkedin → whatsapp). */
export function getDominantTopEngagedChannel(signalCounts) {
  const n = normalizeTopEngagedSignalCounts(signalCounts);
  let bestKey = null;
  let best = -1;
  for (const key of TOP_ENGAGED_METRIC_CHANNELS) {
    const v = n[key];
    if (v > best) {
      best = v;
      bestKey = key;
    }
  }
  if (best <= 0 || bestKey == null) return null;
  return bestKey;
}

/**
 * Compact outreach summary for one channel's `channel_metrics` row
 * ({ sent, opened, clicked, responded }).
 */
export function summarizeChannelMetricsLine(channelMetrics, channelKey) {
  const m =
    channelMetrics && typeof channelMetrics === "object"
      ? channelMetrics[channelKey]
      : null;
  if (!m || typeof m !== "object") return null;
  const sent = Number(m.sent) || 0;
  const opened = Number(m.opened) || 0;
  const clicked = Number(m.clicked) || 0;
  const responded = Number(m.responded) || 0;
  if (!sent && !opened && !clicked && !responded) return null;
  const parts = [];
  if (sent) parts.push(`${sent} sent`);
  if (opened) parts.push(`${opened} open${opened !== 1 ? "s" : ""}`);
  if (clicked) parts.push(`${clicked} click${clicked !== 1 ? "s" : ""}`);
  if (responded) {
    parts.push(`${responded} ${responded === 1 ? "reply" : "replies"}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export function topEngagedChannelTitle(channelKey) {
  return CHANNEL_TITLE[channelKey] ?? channelKey;
}

export function getFollowUpTypeLabel(type) {
  return FOLLOW_UP_TYPE_LABELS[type] ?? "Follow-up";
}

/** One line about an account's follow-up alert reviews, or null when alerts are off. */
export function getFollowUpReviewStatus(alert) {
  if (!alert?.enabled) return null;
  if (["queued", "running"].includes(alert.last_run_status)) {
    return "Reviewing this account's recent conversations...";
  }
  const lastRun =
    alert.last_run_status === "failed"
      ? "Last review failed. Turn alerts off and on to retry now."
      : alert.last_run_at
        ? `Last reviewed ${formatFollowUpDate(alert.last_run_at)}.`
        : null;
  const nextRun = alert.next_run_at
    ? `Next review ${formatFollowUpDate(alert.next_run_at)}.`
    : null;
  return [lastRun, nextRun].filter(Boolean).join(" ");
}

export function formatSignalType(channel, objectType) {
  if (objectType === "UserInvitationSent") return "LinkedIn invite sent";
  if (objectType === "UserInvitationAccepted")
    return "LinkedIn invite accepted";
  if (channel) return `${channel} · ${objectType ?? "signal"}`;
  return objectType ?? "Signal";
}

/** Matches app route: /accounts/:companyDomain/dashboard */
export function getAccountProfilePath(account) {
  const name =
    account?.company_name ??
    account?.account?.company_name ??
    account?.name;
  if (!name || typeof name !== "string") return null;
  const encoded = encodeURIComponent(name.trim());
  if (!encoded) return null;
  return `/accounts/${encoded}/dashboard`;
}
